/**
 * HxStandardTabs Component
 *
 * A simple horizontal tabs component for switching between different content panels. Provides a clean, accessible tab
 * interface with optional icons and standard WAI-ARIA keyboard navigation (ArrowLeft, ArrowRight, Home, End).
 *
 * @example
 *   ```html
 *   <hx-standard-tabs
 *     [tabs]="tabsData"
 *     [activeTab]="activeTabIndex()"
 *     (tabClick)="onTabClick($event)">
 *   </hx-standard-tabs>
 *   ```;
 *
 * @package @complexcare/harmony
 */

import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  output,
  ViewEncapsulation,
} from '@angular/core';
import { HxIcon } from '../icon/icon';
import type { HxStandardTab } from './types';

@Component({
  selector: 'hx-standard-tabs',
  standalone: true,
  imports: [HxIcon],
  templateUrl: './standard-tabs.html',
  styleUrls: ['./standard-tabs.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'hx-standard-tabs',
    '[attr.role]': '"tablist"',
    '(keydown)': 'onKeydown($event)',
  },
})
export class HxStandardTabs {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  // --- INPUTS ---

  /**
   * Array of tab configurations. Each tab should have an index and label, with optional icon.
   *
   * @required
   */
  readonly tabs = input.required<readonly HxStandardTab[]>();

  /**
   * Index of the currently active tab.
   *
   * @default 0
   */
  readonly activeTab = input<number>(0);

  /**
   * Whether to display tabs in a compact/sub-tab style (e.g., for nested tabs).
   *
   * @default false
   */
  readonly compact = input<boolean>(false);

  // --- OUTPUTS ---

  /** Emitted when a tab is clicked or activated via keyboard. Passes the tab index. */
  readonly tabClick = output<number>();

  // --- METHODS ---

  protected onTabClick(index: number): void {
    this.tabClick.emit(index);
  }

  protected trackByIndex(index: number): number {
    return index;
  }

  protected onKeydown(event: KeyboardEvent): void {
    const tabsList = this.tabs();
    if (!tabsList || tabsList.length === 0) {
      return;
    }

    const currentTab = this.activeTab();
    const currentIndex = tabsList.findIndex((t) => t.index === currentTab);
    const validIndex = currentIndex >= 0 ? currentIndex : 0;

    let targetIndex = -1;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        targetIndex = (validIndex + 1) % tabsList.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        targetIndex = (validIndex - 1 + tabsList.length) % tabsList.length;
        break;
      case 'Home':
        targetIndex = 0;
        break;
      case 'End':
        targetIndex = tabsList.length - 1;
        break;
      default:
        return;
    }

    if (targetIndex !== -1) {
      event.preventDefault();
      const targetTab = tabsList[targetIndex];
      this.tabClick.emit(targetTab.index);
      this.focusTab(targetTab.index);
    }
  }

  /** Programmatically focus the tab button with the specified index. */
  focusTab(tabIndex: number): void {
    const button = this.elementRef.nativeElement.querySelector<HTMLButtonElement>(`[data-tab-index="${tabIndex}"]`);
    button?.focus();
  }
}
